import * as subscriptions from '../graphql/subscriptions';
import {
	getPayStationsForAccount,
	getOptimizedPayStationsForAccount,
	getLatestBoxInf,
	getLatestDeviceInf,
	getLatestTerminalInf,
	getLatestSoftwareInf,
	getLatestPlatformInf,
	getLatestTechnicalData,
	listOperations,
	listMessages,
	getLatestInventory,
	getUserGroups,
	getPreferences,
	getAccountOptions,
	getLatestState,
	getLatestPayStationsInformations,
	getTrial,
	setTrial
} from "../graphql/queries";
import GraphQlTool from "./GraphQlTool";
import { UserRoles } from "../Models/Roles";
import UUID from "./UUID";
import { closeDevicePeriod } from "../graphql/mutations";
import { graphqlGated } from "./GraphqlGate";

const factoryUUID = new UUID();

let instanceSyncDataManager;

const performances = {};
const track = step => {
	performances[step] = Date.now();
};

const printPerformancesReport = () => {
	const getTime = key => Number( performances[key] );

	const steps = [
		{
			name: '1) Load Licenses',
			duration: getTime('1_loadLicensesForAccount_end_A') - getTime('1_loadLicensesForAccount_start'),
		},
		{
			name: '2) Load Allowed Boxes',
			duration: getTime('2_loadAllowedBoxes_end_AA') - getTime('2_loadAllowedBoxes_start'),
		},
		{
			name: '3) Load PayStations (Bulk)',
			duration: getTime('3_loadOptimizedPayStationsBulk_end_AB') - getTime('3_loadOptimizedPayStationsBulk_start'),
		},
		{
			name: '4) Format PayStation List',
			duration: getTime('4_formatPayStationList_end_A') - getTime('4_formatPayStationList_start'),
		},
		/*{
			name: '5) Load Latest Informations',
			duration: getTime('5_loadAllLatestInformations_end_A') - getTime('5_loadAllLatestInformations_start'),
		},*/
		{
			name: '5) Load Latest Informations bulk',
			duration: getTime('5A_loadAllLatestInformationsBulk_end_A') - getTime('5A_loadAllLatestInformationsBulk_start'),
		},
		{
			name: '6) Synchronize Loading',
			duration: getTime('6_synchronizeLoading_end_A') - getTime('6_synchronizeLoading_start'),
		},
	];

	const totalDuration = getTime('7_data_loaded') - getTime('1_loadLicensesForAccount_start');
	const subtotalDuration = steps.reduce(
		( sum, { duration } ) => Number.isFinite( duration ) ? sum + duration : sum,
		0
	);

	console.log('—— Performance Report ——');
	console.table(
		steps.map(step => ({
			Step: step.name,
			'Time (ms)': step.duration,
			'% of total': (step.duration / totalDuration * 100).toFixed(1) + '%',
		}))
	);

	console.log('Subtotal of measured steps:', subtotalDuration, 'ms');
	console.log('Total end-to-end duration:', totalDuration, 'ms');
}

class SyncDataManager {
	constructor( cognitoUser , observer ) {
		instanceSyncDataManager = this;
		this.user = cognitoUser;
		this.observer = observer;
		this.queue = [];
		this.subscriptions = [];
		this.notificationHistory = {};

		this.checkTrial()
			.then( isTrialExpired => this.loadLicensesForAccount( isTrialExpired ) )
			.then( licenses => this.loadAllowedBoxes( licenses ) )
			.then( allowedBoxIds => this.loadPayStationsForAccount( allowedBoxIds ) )
			.then( payStations => this.formatPayStationList( payStations ) )
			//.then( payStations => this.loadAllLatestInformations( payStations ) )
			.then( payStations => this.loadAllLatestInformationsBulk( payStations ) )
			.then( payStations => this.synchronizeLoading( payStations ) )
			.then( payStations => {
				//console.log( 'payStations' , payStations );
				if( instanceSyncDataManager.observer &&
					instanceSyncDataManager.observer.onDataLoaded ) {
					instanceSyncDataManager.observer.unrestrictedPayStation = payStations;
					if( this.licenses > -1 && payStations.length > this.licenses && this.isTrialExpired  ) {
						instanceSyncDataManager.observer.onLicenseRequired( payStations.length - this.licenses );
						payStations = payStations.slice( 0, this.licenses );
					}
					track( `7_data_loaded` );
					instanceSyncDataManager.observer.onDataLoaded( this.anonymizePayStationsIfRequired( payStations ) );
					printPerformancesReport();
				}
			} );
	}

	checkTrial() {
		return new Promise( resolve => {
			this.retrieveTrialStartingDate()
				.then( trialData => this.initializeTrialStartingDateIfRequired( trialData ) )
				.then( trialData => {
					if( trialData ) {
						const DAY_IN_MS = 1000 * 60 * 60 * 24;
						const DAY_DURATION = 90;
						const TOTAL_TRIAL_DURATION = DAY_IN_MS * DAY_DURATION;
						const trialStart = new Date( trialData.enrolledAt );
						const trialEnd = new Date( trialStart.getTime() + TOTAL_TRIAL_DURATION );
						this.isTrialExpired = new Date().getTime() > trialEnd.getTime();
					} else {
						this.isTrialExpired = true;
					}
					resolve();
				} );
		} );
	}

	retrieveTrialStartingDate() {
		return new Promise( resolve => {
			graphqlGated( instanceSyncDataManager.observer.props.API,
				{ query: getTrial, variables: { accountId: this.user.accountId }} )
				.then( returned => {
					resolve( returned?.data?.getTrial?.enrolledAt );
				})
				.catch((error) => {
					console.error("error" , error);
					resolve( null );
				});
		} );
	}

	initializeTrialStartingDateIfRequired( trialData ) {
		return new Promise( resolve => {
			if( trialData ) {
				resolve( trialData );
			} else {
				graphqlGated( instanceSyncDataManager.observer.props.API,
					{ query: setTrial, variables: { input: { accountId: this.user.accountId , enrolledAt: new Date().toISOString() }} } )
					.then( returned => {
						resolve( returned?.data?.setTrial?.enrolledAt );
					})
					.catch((error) => {
						console.error("error" , error);
						resolve( null );
					});
			}
		} );
	}

	loadAllowedBoxes( licenses ) {
		track( `2_loadAllowedBoxes_start` );
		this.licenses = licenses;
		return new Promise( resolve => {
			if( instanceSyncDataManager.observer.props.API ) {
				graphqlGated( instanceSyncDataManager.observer.props.API,
					{ query: getUserGroups, variables: { cognitoId: this.user.uuid }} )
					.then( returned => {
						if( returned ) {
							const tool = new GraphQlTool( 'getUserGroups' , returned );
							const allowedBoxIds = tool.extract();
							track( `2_loadAllowedBoxes_end_AA` );
							resolve( allowedBoxIds );
						} else {
							track( `2_loadAllowedBoxes_end_AB` );
							resolve( [] );
						}
					})
					.catch((error) => {
						console.error("error" , error);
						track( `2_loadAllowedBoxes_end_B` );
					})
			} else {
				track( `2_loadAllowedBoxes_end_C` );
				resolve();
			}
		} );
	}

	loadLicensesForAccount( isTrialExpired ) {
		this.isTrialExpired = isTrialExpired;
		track( `1_loadLicensesForAccount_start` );
		return new Promise( resolve => {
			if( instanceSyncDataManager.observer.props.API ) {
				graphqlGated( instanceSyncDataManager.observer.props.API,
					{
						query: getAccountOptions,
						variables: { accountId: this.user.accountId }
					} )
					.then( returned => {
						if( returned ) {
							let licenses = 0;
							if( returned && returned.data && returned.data.getAccountOptions ) {
								try {
									const params = returned.data.getAccountOptions;
									licenses = JSON.parse( params.options ).licenses;
								} catch ( error ) {
									//unused
								}
							}
							track( `1_loadLicensesForAccount_end_A` );
							resolve( licenses );
						}
					})
					.catch((error) => {
						console.error("error" , error);
						track( `1_loadLicensesForAccount_end_B` );
					})
			} else {
				track( `1_loadLicensesForAccount_end_C` );
				resolve();
			}
		} );
	}

	/**
	 * Keeps only backend pay-station records that the current user is allowed to access.
	 *
	 * @param {import('../Models/DomainTypes').EntityId[]} allowedBoxIds
	 * @param {import('../Models/DomainTypes').PayStationRecord[]} accountPayStations
	 * @returns {import('../Models/DomainTypes').PayStationRecord[]}
	 */
	filterPayStationOnUserRestriction( allowedBoxIds , accountPayStations ) {
		const filtered = [];
		accountPayStations.forEach( candidate => {
			if( allowedBoxIds.includes( candidate['box_id'] ) ) {
				filtered.push( candidate );
			}
		} );
		return filtered;
	}

	/**
	 * Removes duplicate backend records by box identifier while preserving their original order.
	 *
	 * @param {import('../Models/DomainTypes').PayStationRecord[]} payStations
	 * @returns {import('../Models/DomainTypes').PayStationRecord[]}
	 */
	filterDistinctBoxes( payStations ) {
		const filtered = [];
		payStations?.forEach( candidate => {
			const existsInFiltered = filtered.some( item => item['box_id'] === candidate['box_id'] );
			if( ! existsInFiltered ) {
				filtered.push( candidate );
			}
		} );
		return filtered;
	}

	/**
	 * Replaces customer-facing names and cashier identities when the connected user has a sales role.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @returns {import('../Models/DomainTypes').PayStation[]}
	 */
	anonymizePayStationsIfRequired( payStations ) {
		const anonymized = {};
		if( this.user.role === UserRoles.SALE_ROLE ) {
			payStations.forEach( payStation => {
				payStation.box.name = `Monnayeur ${payStations.indexOf( payStation ) + 1}`;
				payStation.box.informations.attributes.name = `Monnayeur ${payStations.indexOf( payStation ) + 1}`;

				payStation.terminals.forEach( terminal => {
					terminal.name = `Terminal ${payStation.terminals.indexOf( terminal ) + 1}`;
					terminal.informations.attributes.terminal = `Terminal ${payStation.terminals.indexOf( terminal ) + 1}`;
					terminal.informations.attributes.company = `Société ${payStations.indexOf( payStation ) + 1}`;
					terminal.informations.attributes.shop = `Magasin ${payStations.indexOf( payStation ) + 1}`;

					terminal.operations.forEach( operation => {
						if( ! anonymized[operation.cashier] ) {
							anonymized[operation.cashier] = `Caissier ${Object.keys( anonymized ).length + 1}`;
						}
						operation.cashier = anonymized[operation.cashier];
					} );
				} );
			} );
		}

		return payStations;
	}

	/**
	 * Starts the paginated loading of raw pay-station records for the current account.
	 *
	 * @param {import('../Models/DomainTypes').EntityId[]} allowedBoxIds
	 * @returns {Promise<import('../Models/DomainTypes').PayStationRecord[]>}
	 */
	loadPayStationsForAccount( allowedBoxIds) {
		//track( `3_loadPayStationsForAccount_start` );
		track( `3_loadOptimizedPayStationsBulk_start` );
		return new Promise(resolve => {
			//this.loadPayStationsBulk( resolve , allowedBoxIds, 0, [] );
			this.loadOptimizedPayStationsBulk( resolve , allowedBoxIds, 0, [] );
		});
	}

	/**
	 * Loads the legacy pay-station endpoint page by page and applies user restrictions at completion.
	 *
	 * @param {(payStations: import('../Models/DomainTypes').PayStationRecord[]) => void} resolve
	 * @param {import('../Models/DomainTypes').EntityId[]} allowedBoxIds
	 * @param {number} [offset]
	 * @param {import('../Models/DomainTypes').PayStationRecord[]} [accumulatedPayStations]
	 * @returns {void}
	 */
	loadPayStationsBulk ( resolve , allowedBoxIds, offset = 0, accumulatedPayStations = [] ) {
		if (instanceSyncDataManager.observer.props.API) {
			graphqlGated( instanceSyncDataManager.observer.props.API,
				{
					query: getPayStationsForAccount,
					variables: {
						accountId: this.user.accountId,
						cognitoId: this.user.uuid,
						offset: offset
					}
				} )
				.then( returned => {
					const tool = new GraphQlTool('getPayStationsForAccount', returned);
					const paginatedPayStations = tool.extract();
					const payStations = paginatedPayStations.records;
					accumulatedPayStations = accumulatedPayStations.concat( payStations );
					if ( accumulatedPayStations.length < paginatedPayStations['records_count'] ) {
						instanceSyncDataManager.loadPayStationsBulk( resolve , allowedBoxIds, accumulatedPayStations.length , accumulatedPayStations )
					} else {
						//console.log( 'accumulatedPayStations' , accumulatedPayStations );
						if ( allowedBoxIds && allowedBoxIds.length > 0 ) {
							track( `3_loadPayStationsForAccount_end_AA` );
							resolve( instanceSyncDataManager.filterPayStationOnUserRestriction( allowedBoxIds, accumulatedPayStations ) );
						} else {
							track( `3_loadPayStationsForAccount_end_AB` );
							resolve( accumulatedPayStations );
						}
					}
				})
				.catch(error => {
					track( `3_loadPayStationsForAccount_end_B` );
					resolve( error );
				});
		}
	}

	/**
	 * Loads the optimized pay-station endpoint page by page and applies user restrictions at completion.
	 *
	 * @param {(payStations: import('../Models/DomainTypes').PayStationRecord[]) => void} resolve
	 * @param {import('../Models/DomainTypes').EntityId[]} allowedBoxIds
	 * @param {number} [offset]
	 * @param {import('../Models/DomainTypes').PayStationRecord[]} [accumulatedPayStations]
	 * @returns {void}
	 */
	loadOptimizedPayStationsBulk ( resolve , allowedBoxIds, offset = 0, accumulatedPayStations = [] ) {
		if (instanceSyncDataManager.observer.props.API) {
			graphqlGated( instanceSyncDataManager.observer.props.API,
				{
					query: getOptimizedPayStationsForAccount,
					variables: {
						accountId: this.user.accountId,
						cognitoId: this.user.uuid,
						offset: offset
					}
				} )
				.then( returned => {
					const tool = new GraphQlTool('getOptimizedPayStationsForAccount', returned);
					const paginatedPayStations = tool.extract();
					const payStations = paginatedPayStations.records;
					accumulatedPayStations = accumulatedPayStations.concat( payStations );
					if ( accumulatedPayStations.length < paginatedPayStations['records_count'] ) {
						instanceSyncDataManager.loadOptimizedPayStationsBulk( resolve , allowedBoxIds, accumulatedPayStations.length , accumulatedPayStations )
					} else {
						//console.log( 'accumulatedOptimizedPayStations' , accumulatedPayStations );
						if ( allowedBoxIds && allowedBoxIds.length > 0 ) {
							track( `3_loadOptimizedPayStationsBulk_end_AA` );
							resolve( instanceSyncDataManager.filterPayStationOnUserRestriction( allowedBoxIds, accumulatedPayStations ) );
						} else {
							track( `3_loadOptimizedPayStationsBulk_end_AB` );
							resolve( accumulatedPayStations );
						}
					}
				})
				.catch( error => {
					console.error( error );
					track( `3_loadOptimizedPayStationsBulk_end_B` );
					resolve( error );
				});
		}
	}

	/**
	 * Converts backend records to the stable model consumed by the UI.
	 *
	 * @param {import('../Models/DomainTypes').PayStationRecord[]} payStations
	 * @returns {Promise<import('../Models/DomainTypes').PayStation[]>}
	 */
	formatPayStationList( payStations ) {
		track( `4_formatPayStationList_start` );
		return new Promise( resolve => {
			if( payStations && payStations.length > 0 ) {
				const formatted = [];
				payStations.forEach( payStation => {
					let foundPayStation = this.findPayStationIfExists( payStation["box_id"] , formatted );
					if( ! foundPayStation ) {
						foundPayStation = {
							box: {
								id: payStation["box_id"],
								name: payStation["box_name"],
								active: payStation["box_active"],
								creation: payStation["box_creation"],
								lastUpdate: payStation["box_last_update"],
								informations: null,
								software: null,
								platform: null
							},
							devices: [],
							terminals: []
						};
						formatted.push( foundPayStation );
					}

					//this.addDeviceIfRequired( foundPayStation , payStation );
					//this.addTerminalIfRequired( foundPayStation , payStation );
					payStation.devices.forEach( device => {
						this.addDeviceIfRequired( foundPayStation , device );
					} );

					payStation.terminals.forEach( terminal => {
						this.addTerminalIfRequired( foundPayStation , terminal );
					} );

					if( payStations.indexOf( payStation ) === payStations.length - 1 ) {
						track( `4_formatPayStationList_end_A` );
						resolve( formatted );
					}
				} );
			} else {
				track( `4_formatPayStationList_end_B` );
				resolve([] );
			}
		} );
	}

	/**
	 * Enriches every station through the legacy sequence of individual GraphQL requests.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @returns {Promise<import('../Models/DomainTypes').PayStation[]>}
	 */
	loadAllLatestInformations( payStations ) {
		track( `5_loadAllLatestInformations_start` );
		return new Promise( resolve => {
			if( payStations && payStations.length > 0 ) {
				const done = [];
				payStations.forEach( payStation => {
					instanceSyncDataManager
						.loadLatestInformationsForOnePayStation( payStation )
						.then( () => {
							done.push( payStation );
							if( done.length === payStations.length ) {
								track( `5_loadAllLatestInformations_end_A` );
								resolve( payStations );
							}
						} );
				} );
			} else {
				track( `5_loadAllLatestInformations_end_B` );
				resolve([] );
			}
		} );
	}

	/**
	 * Builds the identity and timestamp pair expected by the latest-information GraphQL query.
	 *
	 * @param {string} bms
	 * @param {import('../Models/DomainTypes').ISODateString} time
	 * @returns {import('../Models/DomainTypes').LatestIdentityInput}
	 */
	createIdentityInput( bms , time ) {
		return { bms , time };
	}

	/**
	 * Builds one bulk-query input from a normalized pay station and all of its modules.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {import('../Models/DomainTypes').LatestPayStationInput}
	 */
	buildLatestInputItem( payStation ) {
		return {
			box:  instanceSyncDataManager.createIdentityInput( payStation.box.name , payStation.box.lastUpdate ),
			devices: payStation.devices.map( device => instanceSyncDataManager.createIdentityInput( device.name , payStation.box.lastUpdate ) ),
			terminals: payStation.terminals.map( terminal => instanceSyncDataManager.createIdentityInput( terminal.name , payStation.box.lastUpdate ) ),
			userIdAndBoxId: `${instanceSyncDataManager.user.uuid}@${payStation.box.id}`
		};
	}

	/**
	 * Estimates how many GraphQL items a station contributes to a bulk request.
	 *
	 * @param {import('../Models/DomainTypes').LatestPayStationInput} input
	 * @returns {number}
	 */
	calculateBulkSize( input ) {
		return 3 + ( input.devices.length * 3 ) + ( input.terminals.length * 2 );
	}

	/**
	 * Loads latest information in bounded batches, then merges each response into the domain objects.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @returns {Promise<import('../Models/DomainTypes').PayStation[]>}
	 */
	loadAllLatestInformationsBulk( payStations ) {
		track( `5A_loadAllLatestInformationsBulk_start` );
		return new Promise( resolve => {
			const maxBulkSize = 1000;
			let currentInputSize = 0;
			if( payStations && payStations.length > 0 && instanceSyncDataManager.observer.props.API ) {
				const done = [];
				let bulk = [];
				payStations.forEach( payStation => {
					//console.log( 'payStation' , payStation );
					const input = instanceSyncDataManager.buildLatestInputItem( payStation );
					bulk.push( input );
					currentInputSize += instanceSyncDataManager.calculateBulkSize( input );
					const isFullBulk = currentInputSize >= maxBulkSize;
					const isLastBulk = payStations.indexOf( payStation ) === payStations.length - 1;
					if( isFullBulk || isLastBulk ) {
						const bulkInput = JSON.parse( JSON.stringify( bulk ) );
						bulk = [];
						currentInputSize = 0;
						//console.log( 'bulkInput' , bulkInput );
						graphqlGated( instanceSyncDataManager.observer.props.API,
							{ query: getLatestPayStationsInformations, variables: { input: { paystations: bulkInput } } } )
							.then( returned => {
								done.push( ...bulkInput );
								if( returned && returned.data && returned.data.latestPayStationsInformations ) {
									//console.log( 'Bulk return' , returned.data.latestPayStationsInformations );
									instanceSyncDataManager.consolidatedPayStations(
										payStations ,
										returned.data.latestPayStationsInformations.paystations
									);
								}

								if( done.length === payStations.length ) {
									track( `5A_loadAllLatestInformationsBulk_end_A` );
									resolve( payStations );
								}
							})
							.catch( error => {
								done.push( ...bulkInput );
								console.error("error" , error);
								if( done.length === payStations.length ) {
									track( `5A_loadAllLatestInformationsBulk_end_A` );
									resolve( payStations );
								}
							})
					}
				} );
			} else {
				track( `5A_loadAllLatestInformationsBulk_end_B` );
				resolve([] );
			}
		} );
	}

	/**
	 * Adds the latest backend information to already normalized pay stations.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @param {import('../Models/DomainTypes').LatestPayStationInformation[]} informationsBulk
	 * @returns {void}
	 */
	consolidatedPayStations( payStations , informationsBulk ) {
		informationsBulk.forEach( candidate => {
			const payStation = payStations.filter( element => element.box.name === candidate.box.bms )[0];
			payStation.box.informations = candidate.box;
			payStation.box.software = candidate.software;
			payStation.box.platform = candidate.platform;

			candidate.devices.forEach( candidateDevice => {
				try {
					const device = payStation.devices.filter( elementDevice => elementDevice.name === candidateDevice.bms )[0];
					if( device ) {
						device.informations = candidateDevice;
						device.lastState = candidate.states.filter( state => state?.cbms === `5_${candidateDevice.bms}` )[0];
						device.latestInventory = candidate.inventories.filter( inventory => inventory?.cbms === `5_${candidateDevice.bms}` )[0];
					}
				} catch ( error ) {
					console.error( 'error' , error );
					console.error( 'candidateDevice' , candidateDevice );
				}
			} );

			candidate.terminals.forEach( candidateTerminal => {
				try {
					const terminal = payStation.terminals.filter( elementTerminal => elementTerminal.name === candidateTerminal.bms )[0];
					if( terminal ) {
						terminal.informations = candidateTerminal;
						terminal.software = candidateTerminal.software;

					}
				} catch ( error ) {
					console.error( 'error' , error );
					console.error( 'candidateDevice' , candidateTerminal );
				}
			} );

			instanceSyncDataManager.loadDailyOperations( payStation.terminals )
				.then( () => instanceSyncDataManager.loadDailyMessages( payStation.devices ) )
				.then( () => {} );

			//console.log( 'found payStation' , payStation );
		} );
	}

	/**
	 * Runs the complete legacy enrichment sequence for a single normalized pay station.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<import('../Models/DomainTypes').PayStation | Record<string, never>>}
	 */
	loadLatestInformationsForOnePayStation( payStation ) {
		return new Promise( resolve => {
			if( payStation ) {
				//const startedAt = Date.now();
				instanceSyncDataManager
					.loadLatestBoxInformations( payStation )
					.then( () => instanceSyncDataManager.loadLatestDevicesInformations( payStation.devices , payStation ) )
					.then( () => instanceSyncDataManager.loadLatestTerminalsInformations( payStation.terminals ) )
					.then( () => instanceSyncDataManager.loadDailyOperations( payStation.terminals ) )
					.then( () => instanceSyncDataManager.loadDailyMessages( payStation.devices ) )
					.then( () => instanceSyncDataManager.loadLatestBoxSoftwareInformations( payStation ) )
					.then( () => instanceSyncDataManager.loadLatestBoxPlatformInformations( payStation ) )
					.then( () => instanceSyncDataManager.loadLatestBoxInventory( payStation ) )
					.then( () => instanceSyncDataManager.loadBoxPreferencesForUser( payStation ) )
					.then( () => {
						//console.log('⏱ loadAllLatestInformations ms = ' , Date.now() - startedAt)
						//console.log( 'payStation' , payStation );
						resolve( payStation );
					} );
			} else {
				resolve({} );
			}
		} );
	}

	/**
	 * Loads the latest identity and attributes reported by the station box.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadLatestBoxInformations( payStation ) {
		return new Promise( resolve => {
			if( payStation.box.name && payStation.box.name.trim() !== "" && payStation.box.lastUpdate ) {
				if( instanceSyncDataManager.observer.props.API ) {
					graphqlGated( instanceSyncDataManager.observer.props.API,
						{ query: getLatestBoxInf, variables: { input: { bms: payStation.box.name, time:payStation.box.lastUpdate } } } )
						.then( returned => {
							if( returned && returned.data && returned.data.getLatestBoxInf ) {
								payStation.box.informations = returned.data.getLatestBoxInf;
							}
							resolve();
						})
						.catch((error) => {
							console.error("error" , error);
							resolve();
						})
				} else {
					resolve();
				}
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Applies the current user's custom box, company, shop and terminal names to a station.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadBoxPreferencesForUser( payStation ) {
		return new Promise( resolve => {
			if( payStation.box.id && instanceSyncDataManager.user.uuid ) {
				if( instanceSyncDataManager.observer.props.API ) {
					graphqlGated( instanceSyncDataManager.observer.props.API,
						{ query: getPreferences, variables: { userIdAndBoxId: `${instanceSyncDataManager.user.uuid}@${payStation.box.id}` } } )
						.then( returned => {
							if( returned && returned.data && returned.data.getPreferences ) {
								const preferences = JSON.parse( returned.data.getPreferences.data );
								if( preferences ) {
									if( payStation.box?.informations?.attributes?.name ) {
										payStation.box.informations.attributes.name = preferences.box;
									} else {
										payStation.box.name = preferences.box;
									}

									if( payStation.terminals && preferences.terminals ) {
										payStation.terminals?.forEach( terminal => {
											const terminalItem = preferences.terminals[payStation.terminals.indexOf(terminal)];
											if( terminal.informations?.attributes?.terminal ) {
												terminal.informations.attributes.terminal = terminalItem?.name;
												terminal.informations.attributes.company = preferences.company;
												terminal.informations.attributes.shop = preferences.shop;
											} else {
												terminal.name = terminalItem?.name;
											}
										} );
									}
								}
							}
							resolve();
						})
						.catch((error) => {
							console.error("error" , error);
							resolve();
						})
				} else {
					resolve();
				}
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads software information referenced by the latest box information.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadLatestBoxSoftwareInformations( payStation ) {
		return new Promise( resolve => {
			if( payStation.box.informations && payStation.box.informations.attributes && payStation.box.informations.attributes.software ) {
				if( instanceSyncDataManager.observer.props.API ) {
					graphqlGated( instanceSyncDataManager.observer.props.API,
						{ query: getLatestSoftwareInf, variables: {
							input: {
								bms: payStation.box.informations.attributes.software.substring( 2 ),
								time:payStation.box.lastUpdate }
						} } )
						.then( returned => {
							if( returned && returned.data && returned.data.getLatestSoftwareInf ) {
								payStation.box.software = returned.data.getLatestSoftwareInf;
							}
							resolve();
						})
						.catch((error) => {
							console.error("error" , error);
							resolve();
						})
				} else {
					resolve();
				}
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads platform information referenced by the latest box information.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadLatestBoxPlatformInformations( payStation ) {
		return new Promise( resolve => {
			if( payStation.box.informations && payStation.box.informations.attributes && payStation.box.informations.attributes.platform ) {
				if( instanceSyncDataManager.observer.props.API ) {
					graphqlGated( instanceSyncDataManager.observer.props.API,
						{ query: getLatestPlatformInf, variables: {
								input: {
									bms: payStation.box.informations.attributes.platform.substring( 2 ),
									time:payStation.box.lastUpdate }
							} } )
						.then( returned => {
							if( returned && returned.data && returned.data.getLatestPlatformInf ) {
								payStation.box.platform = returned.data.getLatestPlatformInf;
							}
							resolve();
						})
						.catch((error) => {
							console.error("error" , error);
							resolve();
						})
				} else {
					resolve();
				}
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads the most recent inventory snapshot for every device attached to the station.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadLatestBoxInventory( payStation ) {
		return new Promise( resolve => {
			if( payStation.devices && payStation.devices.length > 0 ) {
				const loaded = [];
				payStation.devices.forEach( device => {
					if( instanceSyncDataManager.observer.props.API && device.informations ) {
						graphqlGated( instanceSyncDataManager.observer.props.API,
							{ query: getLatestInventory, variables: {
										cbms: `5_${device.informations.bms}`,
										requestDate:new Date().toISOString()
								} } )
							.then( returned => {
								if( returned && returned.data && returned.data.getLatestInventory ) {
									device.latestInventory = returned.data.getLatestInventory;
								}
								loaded.push( device.informations.bms );
								if( loaded.length === payStation.devices.length ) {
									resolve();
								}
							})
							.catch((error) => {
								console.error("error" , error);
								loaded.push( device.informations.bms );
								if( loaded.length === payStation.devices.length ) {
									resolve();
								}
							})
					} else {
						resolve();
					}
				} )
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads identity information and the current state for every device in a station.
	 *
	 * @param {import('../Models/DomainTypes').Device[]} devices
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @returns {Promise<void>}
	 */
	loadLatestDevicesInformations( devices , payStation ) {
		return new Promise( resolve => {
			if( devices && devices.length > 0 ) {
				const done = [];
				devices.forEach( device => {
					const latestUse = instanceSyncDataManager.getLatestUse( device.periods );
					if( device.name && latestUse ) {
						if( instanceSyncDataManager.observer.props.API ) {
							graphqlGated( instanceSyncDataManager.observer.props.API,
								{ query: getLatestDeviceInf, variables: { input: { bms: device.name, time:latestUse } } } )
								.then( returned => {
									if( returned && returned.data && returned.data.getLatestDeviceInf ) {
										device.informations = returned.data.getLatestDeviceInf;
									}
									done.push( device );
									if( done.length === devices.length ) {
										resolve();
									}
								})
								.catch((error) => {
									console.error("error" , error);
									done.push( device );
									if( done.length === devices.length ) {
										resolve();
									}
								})
							instanceSyncDataManager.loadLatestStateForDevice( device , latestUse );

							if( ! payStation?.box?.informations?.attributes?.devices?.includes( `5_${device.name}` ) ) {
								console.log( 'Must close period for ' , device );
								//this.closePeriodForInvalidDevice( device.id );
							}

						} else {
							resolve();
						}
					} else {
						resolve();
					}
				} );
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Starts loading the most recent state reported by one device.
	 *
	 * @param {import('../Models/DomainTypes').Device} device
	 * @param {import('../Models/DomainTypes').ISODateString | null} latestUse
	 * @returns {void}
	 */
	loadLatestStateForDevice( device , latestUse ) {
		if( device.name && latestUse ) {
			if( instanceSyncDataManager.observer.props.API ) {
				graphqlGated( instanceSyncDataManager.observer.props.API,
					{ query: getLatestState, variables: { cbms: `5_${device.name}`, at:latestUse } } )
					.then( returned => {
						if( returned && returned.data && returned.data.getLatestState ) {
							device.lastState = returned.data.getLatestState;
						}
					})
					.catch((error) => {
						console.error("error" , error);
					})
			}
		}
	}

	/**
	 * Loads identity and software information for every terminal in the supplied list.
	 *
	 * @param {import('../Models/DomainTypes').Terminal[]} terminals
	 * @param {unknown} [show]
	 * @returns {Promise<void>}
	 */
	loadLatestTerminalsInformations( terminals , show ) {
		return new Promise( resolve => {
			if( terminals && terminals.length > 0 ) {
				const done = [];
				terminals.forEach( terminal => {
					const latestUse = instanceSyncDataManager.getLatestUse( terminal.periods );
					if( terminal.name && latestUse ) {
						if( instanceSyncDataManager.observer.props.API ) {
							graphqlGated( instanceSyncDataManager.observer.props.API,
								{ query: getLatestTerminalInf, variables: { input: { bms: terminal.name, time:latestUse } } } )
								.then( returned => {
									if( returned && returned.data && returned.data.getLatestTerminalInf ) {
										terminal.informations = returned.data.getLatestTerminalInf;
										graphqlGated( instanceSyncDataManager.observer.props.API,
											{ query: getLatestSoftwareInf, variables: {
													input: {
														bms: terminal.informations.attributes.software.substring( 2 ),
														time: new Date().toISOString() }
												} } )
											.then( returned2 => {
												terminal.software = returned2.data.getLatestSoftwareInf;
											})
											.catch( error => {
												console.error("error" , error);
											})
									}
									done.push( terminal );
									if( done.length === terminals.length ) {
										resolve();
									}
								})
								.catch( error => {
									console.error("error" , error);
									done.push( terminal );
									if( done.length === terminals.length ) {
										resolve();
									}
								})
						} else {
							resolve();
						}
					} else {
						resolve();
					}
				} );
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Clears and reloads today's operations for every supplied terminal.
	 *
	 * @param {import('../Models/DomainTypes').Terminal[]} terminals
	 * @param {unknown} [show]
	 * @returns {Promise<void>}
	 */
	loadDailyOperations( terminals , show ) {
		return new Promise( resolve => {
			if( terminals && terminals.length > 0 ) {
				terminals.forEach( terminal => {
					terminal.operations = [];
					if( terminal.informations || terminal.name ) {
						const start = new Date();
						start.setHours( 0 , 0 , 0 , 0 );
						const end = new Date();
						end.setHours( 23 , 59 , 59 , 999 );
						const bms = ( terminal.informations?.bms ) ? terminal.informations.bms : terminal.name
						this.loadOperations( terminal , `6_${bms}` , {
							start: start.toISOString(),
							end: end.toISOString()
						} , null );
					}
					resolve();
				} );
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads one page of operations for a terminal and follows the pagination token recursively.
	 *
	 * @param {import('../Models/DomainTypes').Terminal} terminal
	 * @param {string} cbms
	 * @param {import('../Models/DomainTypes').QueryPeriod} period
	 * @param {string | null} nextToken
	 * @returns {void}
	 */
	loadOperations( terminal , cbms , period , nextToken ) {
		if( ! instanceSyncDataManager.queue.includes( cbms ) ) {
			instanceSyncDataManager.queue.push( cbms );
		}

		if( instanceSyncDataManager.observer.props.API ) {
			graphqlGated( instanceSyncDataManager.observer.props.API,
				{ query: listOperations, variables: { input: { cbms: cbms , count: 200 , start: period.start , end: period.end, nextToken: nextToken } } } )
				.then( returned => {
					if( returned && returned.data && returned.data.listOperations ) {
						returned.data.listOperations.operations.forEach( operation => {
							terminal.operations.push( operation );
						} );
						if( returned.data.listOperations.nextToken ) {
							this.loadOperations( terminal , cbms , period , returned.data.listOperations.nextToken );
						} else {
							if( instanceSyncDataManager.queue.includes( cbms ) ) {
								instanceSyncDataManager.queue.splice( instanceSyncDataManager.queue.indexOf( cbms ) , 1 );
							}
						}
					}
				})
				.catch( error => {

				})
		}
	}

	/**
	 * Clears and reloads today's messages for every supplied device.
	 *
	 * @param {import('../Models/DomainTypes').Device[]} devices
	 * @param {unknown} [show]
	 * @returns {Promise<void>}
	 */
	loadDailyMessages( devices , show ) {
		return new Promise( resolve => {
			if( devices && devices.length > 0 ) {
				devices.forEach( device => {
					device.messages = [];
					const start = new Date();
					start.setHours( 0 , 0 , 0 , 0 );
					const end = new Date();
					end.setHours( 23 , 59 , 59 , 999 );
					this.loadMessages( device , `5_${device.informations?.bms}` , {
						start: start.toISOString(),
						end: end.toISOString()
					} , null );
					resolve();
				} );
			} else {
				resolve();
			}
		} );
	}

	/**
	 * Loads one page of messages for a device and follows the pagination token recursively.
	 *
	 * @param {import('../Models/DomainTypes').Device} device
	 * @param {string} cbms
	 * @param {import('../Models/DomainTypes').QueryPeriod} period
	 * @param {string | null} nextToken
	 * @returns {void}
	 */
	loadMessages( device , cbms , period , nextToken ) {
		if( instanceSyncDataManager.observer.props.API ) {
			graphqlGated( instanceSyncDataManager.observer.props.API ,
				{ query: listMessages, variables: { input: { cbms: cbms , count: 200 , start: period.start , end: period.end, nextToken: nextToken } } } )
				.then( returned => {
					if( returned && returned.data && returned.data.listMessages ) {
						returned.data.listMessages.messages.forEach( message => {
							if( !message.level ) {
								message.level = 0;
							}
							device.messages.push( message );
						} );
						if( returned.data.listMessages.nextToken ) {
							this.loadMessages( device , cbms , period , returned.data.listMessages.nextToken );
						}
					}
				})
				.catch( error => {
					console.log( error );
				})
		}
	}

	/**
	 * Starts loading the latest raw technical-data payload for one device.
	 *
	 * @param {import('../Models/DomainTypes').Device} device
	 * @returns {void}
	 */
	loadTechnicalDataForDevice( device ) {
		const latestUse = instanceSyncDataManager.getLatestUse( device.periods );
		if( device.name && latestUse ) {
			if( instanceSyncDataManager.observer.props.API ) {
				graphqlGated( instanceSyncDataManager.observer.props.API ,
					{ query: getLatestTechnicalData, variables: { cbms: `5_${device.name}`, updatedAt:latestUse } } )
					.then( returned => {
						if( returned && returned.data && returned.data.getLatestTechnicalData ) {
							device.technicalData = returned.data.getLatestTechnicalData;
						}
					})
					.catch( error => {
						console.error("error" , error);
					})
			}
		}
	}

	/**
	 * Returns the end of the latest closed period, or the current time when a period is still open.
	 *
	 * @param {import('../Models/DomainTypes').Period[]} periods
	 * @returns {import('../Models/DomainTypes').ISODateString | null}
	 */
	getLatestUse( periods ) {
		let latest = null;
		if( periods && periods.length > 0 ) {
			periods.forEach( period => {
				const hasOpenPeriod = period.endedAt === null;
				const canUpdateLatest = ! latest || period.endedAt > latest;
				if( hasOpenPeriod ) {
					latest = new Date().toISOString();
				} else if ( canUpdateLatest ) {
					latest = period.endedAt;
				}
			} );
		}
		return latest;
	}

	/**
	 * Finds a normalized pay station by its box identifier.
	 *
	 * @param {import('../Models/DomainTypes').EntityId} boxId
	 * @param {import('../Models/DomainTypes').PayStation[]} list
	 * @returns {import('../Models/DomainTypes').PayStation | null}
	 */
	findPayStationIfExists( boxId , list ) {
		let found = null;
		if( list && list.length > 0 ) {
			list.forEach( candidate => {
				if( candidate.box.id === boxId ) {
					found = candidate;
				}
			} );
		}
		return found;
	}

	/**
	 * Adds a device from a backend record, or appends a missing activity period to an existing device.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @param {import('../Models/DomainTypes').DeviceRecord} record
	 * @returns {void}
	 */
	addDeviceIfRequired( payStation , record ) {
		const deviceFound = payStation.devices.find(
			device => device.id === record["device_id"]
		) ?? null;

		if( ! record["device_name"].endsWith( '_' )  ) {
			if( ! deviceFound && record["device_id"] ) {
				const device = {
					id: record["device_id"],
					name: record["device_name"],
					active: record["device_active"],
					periods: [{ startedAt: record["device_period_start"] , endedAt: record["device_period_end"] }],
					informations: null,
					lastState: null,
					latestInventory: null,
					technicalData: null,
					messages: []
				}
				payStation.devices.push( device );
				this.loadTechnicalDataForDevice( device );
			} else {
				if( record["device_id"] && deviceFound && ! this.periodExists( { startedAt: record["device_period_start"] ,
					endedAt: record["device_period_end"] } , deviceFound.periods ) ) {
					deviceFound.periods.push({ startedAt: record["device_period_start"] , endedAt: record["device_period_end"] });
				}
			}
		} else {
			console.log( '[unrecognized record]' , record );
			//this.closePeriodForInvalidDevice( record["device_id"] );
		}
	}

	closePeriodForInvalidDevice( deviceId ) {
		if( instanceSyncDataManager.observer.props.API ) {
			graphqlGated( instanceSyncDataManager.observer.props.API , { query: closeDevicePeriod, variables: { deviceId: deviceId } } )
				.then( returned => {
					console.log( 'returned' , returned );
				})
				.catch((error) => {
					console.error("error" , error);
				})
		}
	}

	/**
	 * Adds a terminal from a backend record, or appends a missing activity period to an existing terminal.
	 *
	 * @param {import('../Models/DomainTypes').PayStation} payStation
	 * @param {import('../Models/DomainTypes').TerminalRecord} record
	 * @returns {void}
	 */
	addTerminalIfRequired( payStation , record ) {
		const terminalFound = payStation.terminals.find(
			terminal => terminal.id === record["terminal_id"]
		) ?? null;

		if( ! terminalFound && record["terminal_id"] ) {
			payStation.terminals.push( {
				id: record["terminal_id"],
				name: record["terminal_name"],
				active: record["terminal_active"],
				periods: [{ startedAt: record["terminal_period_start"] , endedAt: record["terminal_period_end"] }],
				informations: null,
				software: null,
				operations: []
			} );
		} else {
			if( record["terminal_id"] && terminalFound && ! this.periodExists( { startedAt: record["terminal_period_start"] ,
												endedAt: record["terminal_period_end"] } , terminalFound.periods ) ) {
				terminalFound.periods.push({ startedAt: record["terminal_period_start"] , endedAt: record["terminal_period_end"] });
			}
		}
	}

	/**
	 * Checks whether an activity period with the same boundaries is already registered.
	 *
	 * @param {import('../Models/DomainTypes').Period} candidate
	 * @param {import('../Models/DomainTypes').Period[]} periods
	 * @returns {boolean}
	 */
	periodExists( candidate , periods ) {
		let exists = false;
		if( periods && candidate && periods.length > 0 ) {
			periods.forEach( period => {
				if( ! exists &&
					period.startedAt === candidate.startedAt &&
					period.endedAt === candidate.endedAt ) {
					exists = true;
				}
			} );
		}
		return exists;
	}

	/**
	 * Waits for queued operation requests before exposing the fully loaded pay-station list.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @returns {Promise<import('../Models/DomainTypes').PayStation[]>}
	 */
	synchronizeLoading( payStations ) {
		track( `6_synchronizeLoading_start` );
		return new Promise( resolve => {
			if ( instanceSyncDataManager.queue.length > 0 ) {
				instanceSyncDataManager.waitQueueResolution( payStations , resolve );
			} else {
				track( `6_synchronizeLoading_end_B` );
				resolve( payStations );
			}
		} );
	}

	/**
	 * Polls the request queue and resolves the loading promise as soon as it becomes empty.
	 *
	 * @param {import('../Models/DomainTypes').PayStation[]} payStations
	 * @param {(payStations: import('../Models/DomainTypes').PayStation[]) => void} resolve
	 * @returns {void}
	 */
	waitQueueResolution( payStations , resolve ) {
		if ( instanceSyncDataManager.queue.length > 0 ) {
			setTimeout( () => {
				instanceSyncDataManager.waitQueueResolution( payStations , resolve );
			} , 500 );
		} else {
			track( `6_synchronizeLoading_end_A` );
			resolve( payStations );
		}
	}

	subscribe() {
		this.unsubscribe();
		const registrations = [
			{
				query: subscriptions.onNewState,
				type: 'onNewState'
			},{
				query: subscriptions.onNewTechnicalData,
				type: 'onNewTechnicalData'
			},{
				query: subscriptions.onNewOperation,
				type: 'onNewOperation'
			},{
				query: subscriptions.onNewInventory,
				type: 'onNewInventory'
			},{
				query: subscriptions.onNewNotificationState,
				type: 'onNewNotificationState'
			}
		];
		//onNotificationProgressReceived
		if( this.subscriptions.length < registrations.length ) {
			registrations.forEach( registration => {
				this.notificationHistory[registration.type] = { queue: [] , done: [] };
				this.subscriptions.push( this.getSubscription( registration.query , registration.type ) );
			} );
		}
	}

	getSubscription( subscriptionQuery , type ) {
		return this.observer.props.API
			.graphql({ query: subscriptionQuery })
			.subscribe({
				next: ({ data }) => {
					this.notificationHistory[type].queue.push( data[type] );
					this.notifyForQueue( type ).then( () => {} );
				},
				error: error => {
					this.observer.onSubscriptionError( error );
				}
			});
	}

	notify( type ) {
		return new Promise( resolve => {
			if( instanceSyncDataManager.notificationHistory[type].queue.length > 0 &&
				instanceSyncDataManager.notificationHistory[type].queue[0] ) {
				const item = instanceSyncDataManager.notificationHistory[type].queue[0];
				let uuid = ( item.uuid ) ? item.uuid : item.jobId;
				if( ! uuid ) {
					uuid = factoryUUID.generate();
				}
				if( this.notificationHistory[type].done.includes( uuid ) ) {
					instanceSyncDataManager.notificationHistory[type].queue.shift();
					resolve();
				} else {
					this.observer.onNewSubscriptionReceived( type , item );
					instanceSyncDataManager.notificationHistory[type].done.push( uuid );
					instanceSyncDataManager.notificationHistory[type].queue.shift();
					resolve();
				}
			} else {
				if( ! instanceSyncDataManager.notificationHistory[type].queue[0]  ) {
					instanceSyncDataManager.notificationHistory[type].queue.shift();
				}
				resolve();
			}
		} );
	}

	notifyForQueue( type , resolveRecursive ) {
		return new Promise( resolve => {
			if ( instanceSyncDataManager.notificationHistory[type].queue.length > 0 ) {
				instanceSyncDataManager.notify( type )
					.then( () => {
						instanceSyncDataManager.notifyForQueue( type , resolveRecursive )
							.then( () => {
								resolve();
							} );
					} );
			} else {
				if( resolveRecursive ) {
					resolveRecursive();
				} else {
					resolve();
				}
			}
		} );
	}

	unsubscribe() {
		if( this.subscriptions ) {
			this.subscriptions.forEach( subscription => {
				subscription.unsubscribe();
			} );
			this.subscriptions = [];
		}
	}
}

export default SyncDataManager;
